import { v4 } from 'uuid';

import { setAggregateDataVersions, upsertAggregateData } from 'api/ClinicalNote';
import { deleteMultipleCustomFormResponse } from 'api/CustomForms';
import { deleteMultipleMedications } from 'api/Medication';

import { formatDate } from 'lib/helpers/dateUtils';
import { ia, iaRa } from 'lib/helpers/utility';

import { deletedCptLoadNoteKeys } from '../../../lib/constants';

export const cleanUpVitals = ({ data = {} }) => {
  delete data?.created_at;
  const updatedVitals = data?.vitals?.map((vital) => {
    delete vital?.id;
    delete vital?.createdBy;
    return vital;
  });

  return {
    ...data,
    vitals: updatedVitals
  };
};

export const loadFromPreviousNoteIcds = ({
  icd10Codes,
  prevAppointmentId,
  newAppointmentId,
  hasICDs
}) => {
  if (!ia(icd10Codes)) return [];

  if (!hasICDs) return iaRa(icd10Codes);

  return iaRa(icd10Codes)
    .filter((item) => item?.appointment_id == prevAppointmentId)
    .map((item) => ({
      ...item,
      appointment_id: newAppointmentId
    }));
};

export const cleanUpCPTCodes = ({ cptCodes = [], timezone, dos }) => {
  return iaRa(cptCodes)?.map((cpt) => {
    const cleanedCpt = structuredClone(cpt);

    deletedCptLoadNoteKeys?.forEach((key) => {
      delete cleanedCpt?.[key];
    });

    return {
      ...cleanedCpt,
      uuid: v4(),
      created_at: formatDate(new Date(), timezone, 'MM/dd/yyyy h:mm a zzz'),
      from_date: dos,
      thru_date: dos
    };
  });
};

export const loadPreviousAggregateData = async ({
  oldData,
  newData,
  patientId,
  appointmentId,
  clinicalNoteId,
  navigate
}) => {
  const aggregatedTypes = [
    'allergies',
    'vaccines',
    'familyHistory',
    'surgicalHistory',
    'medicationHistory'
  ];

  const deletedMedications = [];
  const deletedCFRs = [];
  const updatedEntries = [];

  aggregatedTypes.forEach((type) => {
    const oldItems = iaRa(oldData?.[type]);
    const newItems = iaRa(newData?.[type]);

    const isMedicationHistory = type === 'medicationHistory';

    oldItems.forEach((oldItem) => {
      const index = newItems.findIndex((newItem) => newItem?.id === oldItem?.id);
      const isMissing = index === -1;

      const isOtherVersion = !isMissing && newItems[index]?.version !== oldItem?.version;

      if (isMissing) {
        if (isMedicationHistory) {
          deletedMedications.push(oldItem);
        } else {
          deletedCFRs.push([oldItem?.id, oldItem?.version]);
        }
      } else if (isOtherVersion) {
        const resourceType = isMedicationHistory ? 'medications' : type;

        updatedEntries.push({
          resourceId: oldItem?.id,
          resourceType,
          version: newItems[index]?.version
        });

        if (isMedicationHistory && oldItem?.weight_loss_medication_id) {
          updatedEntries.push({
            resourceId: oldItem?.id,
            resourceType: 'weightLossAdministration',
            version: newItems[index]?.version
          });
        }
      }
    });
  });

  const queries = [];

  if (ia(deletedMedications)) {
    queries.push(
      deleteMultipleMedications(navigate, {
        medications: deletedMedications,
        clinicalNoteId,
        appointmentId,
        patientId
      })
    );
  }

  if (ia(updatedEntries)) {
    queries.push(
      setAggregateDataVersions(navigate, {
        patientId,
        clinicalNoteId,
        resources: updatedEntries
      })
    );
  }

  if (ia(deletedCFRs)) {
    queries.push(
      deleteMultipleCustomFormResponse(navigate, {
        customFormIdVersionPairs: deletedCFRs,
        clinicalNoteId,
        appointmentId
      })
    );
  }

  await Promise.all(queries);

  await upsertAggregateData(navigate, {
    patientId,
    clinicalNoteId,
    appointmentId,
    data: newData
  });
};
